import React from "react";
import { MdOutlineCloudDownload } from "react-icons/md";
import { toast } from "react-hot-toast";
import { BiPlus } from "react-icons/bi";
import Layout from "../../Layout";
import { Button } from "../../components/Form";
import { DoctorsTable } from "../../components/Tables";
import { useNavigate } from "react-router-dom";
import AddDoctorModal from "../../components/Modals/AddDoctorModal";
import { Filter } from "lucide-react";
import { BaseUrl } from "../../BaseUrl";
import axios from "axios";
import DoctorProfile from "./DoctorProfile";

function Doctors() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [data, setData] = React.useState({});
  const [professionalUsers, setProfessionalUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState({
    category: "All",
    status: "All",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalUsers, setTotalUsers] = React.useState(0);
  const itemsPerPage = 10;

  const navigate = useNavigate();

  const onCloseModal = () => {
    setIsOpen(false);
    setData({});
    // Refresh the list after modal closes
    fetchProfessionalUsers();
  };

  const fetchProfessionalUsers = React.useCallback(async () => {
    try {
      setLoading(true);

      // Build query with pagination
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const response = await axios.get(
        `${BaseUrl}/facility/getAllProfessionalUsers?${params.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        // Transform API data to match DoctorsTable format
        const transformedData = response.data.data.map((user, index) => {
          // Generate avatar with first letter of name if no profile image
          const fullName = user.fullName || "Unknown";
          const nameParts = fullName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts[1] || "";
          const avatarUrl =
            user.userImage ||
            user.doctorImage ||
            `https://api.dicebear.com/5.x/initials/svg?seed=${firstName}_${lastName}`;

          return {
            id: user._id || index + 1,
            _id: user._id,
            user: {
              image: avatarUrl,
              title: fullName,
              phone: user.mobileNumber || "N/A",
              email: user.emailId || "N/A",
            },
            title:
              user.category === "Doctor"
                ? "Dr."
                : user.category || "Professional",
            role: user.role, // Store role for reference
            category: user.category,
            subCategory: user.subCategory,
            // hprId: user.hprId,
            dateOfBirth: user.dateOfBirth,
            createdAt: user.createdAt, // Include createdAt for date formatting
            joinedAt: user.createdAt, // Also include as joinedAt
            fullName: user.fullName,
            mobileNumber: user.mobileNumber,
            emailId: user.emailId,
            gender: user.gender,
            specialization: user.specialization,
          };
        });
        setProfessionalUsers(transformedData);

        // Set pagination info from response
        if (response.data.totalPages) {
          setTotalPages(response.data.totalPages);
          setTotalUsers(response.data.totalUsers || transformedData.length);
        } else {
          setTotalUsers(transformedData.length);
          setTotalPages(Math.ceil(transformedData.length / itemsPerPage) || 1);
        }
      } else {
        toast.error("Failed to fetch professional users");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error loading professional users"
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  React.useEffect(() => {
    fetchProfessionalUsers();
  }, [fetchProfessionalUsers]);

  const preview = (data) => {
    navigate(`/doctors/preview/${data._id || data.id}`, {
      state: { doctorData: data }, // optional but VERY useful
    });
  };

  const handleDelete = async (professionalData) => {
    try {
      const response = await axios.delete(
        `${BaseUrl}/facility/deleteStaff/${professionalData._id || professionalData.id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        const name =
          professionalData.fullName ||
          professionalData.user?.title ||
          "professional";
        toast.success(`Deleted ${name}`);
        // Refresh the list
        fetchProfessionalUsers();
      } else {
        toast.error(response.data.message || "Failed to delete professional");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete professional"
      );
    }
  };

  // Filter professional users based on search query and filters
  const filteredProfessionalUsers = professionalUsers.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      user.user.title.toLowerCase().includes(searchLower) ||
      user.user.email.toLowerCase().includes(searchLower) ||
      user.user.phone.toLowerCase().includes(searchLower);
    // (user.hprId && user.hprId.toLowerCase().includes(searchLower));

    const matchesCategory =
      filters.category === "All" || user.category === filters.category;

    // Status filter can be based on active/inactive or other criteria
    // For now, we'll assume all are active unless specified
    const matchesStatus = filters.status === "All" || true; // Add status logic if needed

    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <Layout>
      {/* Add Professional Modal */}
      {isOpen && (
        <AddDoctorModal
          closeModal={onCloseModal}
          isOpen={isOpen}
          doctor={true}
          datas={data}
        />
      )}
      {/*  */}
      <div className='flex flex-row items-end'>
        <h1 className='text-[38px] font-[600] text-black'>avijo</h1>
        <h6 className='text-[24px] font-[500] italic text-[#FD7979]'>
          Experts
        </h6>
      </div>
      <div
        data-aos='fade-up'
        data-aos-duration='1000'
        data-aos-delay='100'
        data-aos-offset='200'
        className='my-8 rounded-xl border-[1px] border-border bg-white p-5'
      >
        {/* datas */}

        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-6'>
          <div className='grid items-center gap-6 md:col-span-4 lg:grid-cols-4'>
            <input
              type='text'
              placeholder='Search professionals '
              className='h-14 w-full rounded-md border border-border bg-dry px-4 text-sm text-main'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Add Professional Button */}
          <Button
            label='Add Professional'
            Icon={BiPlus}
            onClick={() => {
              setData({});
              setIsOpen(true);
            }}
          />

          {/* Filter Button */}
          <Button
            label='Filter'
            Icon={Filter}
            onClick={() => {
              setShowFilters(!showFilters);
            }}
          />
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className='mt-4 grid grid-cols-1 gap-4 rounded-lg border border-border bg-dry p-4 sm:grid-cols-2 md:grid-cols-3'>
            <div>
              <label className='mb-2 block text-sm font-semibold text-main'>
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) =>
                  setFilters({ ...filters, category: e.target.value })
                }
                className='h-12 w-full rounded-md border border-border bg-white px-4 text-sm text-main focus:border-subMain focus:outline-none'
              >
                <option value='All'>All Categories</option>
                <option value='Doctor'>Doctor</option>
                <option value='Nurse'>Nurse</option>
              </select>
            </div>
            <div>
              <label className='mb-2 block text-sm font-semibold text-main'>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className='h-12 w-full rounded-md border border-border bg-white px-4 text-sm text-main focus:border-subMain focus:outline-none'
              >
                <option value='All'>All Status</option>
                <option value='Active'>Active</option>
                <option value='Inactive'>Inactive</option>
              </select>
            </div>
            <div className='flex items-center'>
              <button
                onClick={() => {
                  setFilters({ category: "All", status: "All" });
                  setShowFilters(false);
                }}
                className='mt-7 h-12 w-full rounded-md border border-border bg-white px-4 text-sm text-main hover:bg-gray-50'
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
        <div className='mt-8 w-full overflow-x-scroll'>
          {loading ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>Loading professionals...</p>
            </div>
          ) : filteredProfessionalUsers.length === 0 ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>
                {searchQuery
                  ? "No professionals found matching your search."
                  : "No professionals found."}
              </p>
            </div>
          ) : (
            <>
              <DoctorsTable
                doctor={true}
                data={filteredProfessionalUsers}
                functions={{
                  preview: preview,
                  delete: handleDelete,
                }}
              />

              {/* Pagination */}
              <div className='mt-6 flex items-center justify-between'>
                <p className='text-sm text-textGray'>
                  Showing page {currentPage} of {totalPages} ({totalUsers}{" "}
                  professionals)
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                      currentPage === 1
                        ? "cursor-not-allowed border-gray-300 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    ←
                  </button>
                  <span className='rounded-md bg-subMain bg-opacity-10 px-4 py-2 text-sm font-medium text-main'>
                    {currentPage}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage >= totalPages}
                    className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                      currentPage >= totalPages
                        ? "cursor-not-allowed border-gray-300 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Doctors;
